/**
 * The only place on this screen where the word "offline" appears, and the reason it
 * is a separate region rather than four more numbers in the presence tile.
 *
 * `Device.liveness` is computed by Task 19 from `last_heartbeat_at` on **every
 * read**, is never stored, and lags **pessimistically**: a machine is called offline
 * slightly before it truly is, never slightly after. `stale` and `offline` are kept
 * apart there and are kept apart here — folding them would be the same mistake as
 * `GET /devices?online=false`, which the contract itself warns about.
 *
 * Three reasons this is not merged into the headcount:
 *
 *  1. **It counts machines.** One person may have several and one may have none, so
 *     "12 offline" is not "12 people away from their desks".
 *  2. **It needs `workforce.manage_devices`**, which is in the `ceo` floor and not
 *     in the `manager` floor. A manager gets a sentence naming the permission — not
 *     a zero, and not a silent omission, because an omitted region reads as "there
 *     is nothing to say" and a zero reads as "the fleet is fine".
 *  3. **`never reported` is its own count.** A device that has never sent a
 *     heartbeat is not an offline device; it is a device nobody has heard from, and
 *     `AgentOffline` has three sentences for exactly this reason and no numeric prop
 *     at all.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { LivenessCensus } from '../../overview/fold';
import { PermissionDenied } from '../../states/PermissionDenied';

export function DeviceStrip({
  census,
  permitted,
}: {
  readonly census: LivenessCensus | null;
  readonly permitted: boolean;
}): ReactNode {
  const { t } = useI18n();

  if (!permitted) {
    return (
      <section className="wp-tile" data-testid="k4-devices" aria-labelledby="k4-devices-title">
        <h3 className="wp-tile__title" id="k4-devices-title">
          {t('overview.devices.title')}
        </h3>
        <p className="wp-small" data-testid="k4-devices-denied">
          {t('overview.devices.denied')}
        </p>
        <PermissionDenied code="WORKFORCE_PERMISSION_DENIED" testId="k4-devices-permission" />
      </section>
    );
  }

  if (census === null) return null;

  return (
    <section className="wp-tile" data-testid="k4-devices" aria-labelledby="k4-devices-title">
      <h3 className="wp-tile__title" id="k4-devices-title">
        {t('overview.devices.title')}
      </h3>
      <p className="wp-small wp-muted" data-testid="k4-devices-note">
        {t('overview.devices.note')}
      </p>
      <ul className="wp-breakdown">
        <li data-testid="k4-devices-online">
          <span className="wp-breakdown__count">{census.online}</span>{' '}
          {t('overview.devices.online')}
        </li>
        <li data-testid="k4-devices-stale">
          <span className="wp-breakdown__count">{census.stale}</span> {t('overview.devices.stale')}
        </li>
        <li data-testid="k4-devices-offline">
          <span className="wp-breakdown__count">{census.offline}</span>{' '}
          {t('overview.devices.offline')}
        </li>
        <li data-testid="k4-devices-never">
          <span className="wp-breakdown__count">{census.neverReported}</span>{' '}
          {t('overview.devices.never')}
        </li>
      </ul>
      <p className="wp-small wp-muted" data-testid="k4-devices-stale-help">
        {t('overview.devices.staleHelp')}
      </p>
      {census.known < census.total ? (
        <p className="wp-small wp-muted" data-testid="k4-devices-truncated">
          {t('overview.devices.truncated', { known: census.known, total: census.total })}
        </p>
      ) : null}
    </section>
  );
}
